import React, { useMemo, useState } from 'react';

function humanDuration(openedAt) {
  if (!openedAt) return null;
  const mins = Math.floor((Date.now() - new Date(openedAt).getTime()) / 60000);
  if (mins < 1) return '< 1m';
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) { const h = Math.floor(mins / 60); const m = mins % 60; return m > 0 ? `${h}h ${m}m` : `${h}h`; }
  const days = Math.floor(mins / 1440);
  const hrs = Math.floor((mins % 1440) / 60);
  return hrs > 0 ? `${days}d ${hrs}h` : `${days}d`;
}

const STATUS_CFG = {
  occupied:      { headerBg: '#dc2626', border: '#fca5a5', label: 'Occupied'      },
  needs_cleared: { headerBg: '#d97706', border: '#fcd34d', label: 'Needs Cleared' },
  vacant:        { headerBg: '#16a34a', border: '#86efac', label: 'Vacant'        },
};

function PaxIndicator({ pax, capacity }) {
  const cap = Math.min(capacity || 4, 8);
  const filled = Math.min(pax || 0, cap);
  return (
    <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', maxWidth: 64, justifyContent: 'flex-end' }}>
      {Array.from({ length: cap }, (_, i) => (
        <div key={i} style={{
          width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
          background: i < filled ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.28)',
        }} />
      ))}
    </div>
  );
}

export default function TableSelector({
  tables,
  allTableSessions,
  recentOrders,
  onSelect,
  onBack,
  isMobile,
  onGenerateTableBill,
  onPrintKot,
}) {
  const [search, setSearch] = useState('');
  const [clearedTids, setClearedTids] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState('FLOOR'); // 'FLOOR' | 'LIST'

  // Tables where ALL active orders today are READY and unbilled
  const tableReadyToBill = useMemo(() => {
    const map = {};
    const today = new Date().toISOString().slice(0, 10);
    const active = new Set(['OPEN', 'PARTIAL_KOT', 'KOT_SENT', 'PREPARING', 'READY']);
    const grouped = {};
    for (const o of (recentOrders || [])) {
      const tid = String(o.table_id || '');
      if (!tid) continue;
      if (!active.has(String(o.status || '').toUpperCase())) continue;
      if ((o.business_date || o.opened_at || '').toString().slice(0, 10) !== today) continue;
      if (!grouped[tid]) grouped[tid] = [];
      grouped[tid].push(o);
    }
    for (const [tid, orders] of Object.entries(grouped)) {
      if (orders.length > 0 && orders.every(o => o.status === 'READY') && orders.every(o => !o.bill_id)) {
        map[tid] = orders.map(o => o.id);
      }
    }
    return map;
  }, [recentOrders]);

  // Per-table rich status: occupied | needs_cleared | vacant
  const statusMap = useMemo(() => {
    const map = {};
    const today = new Date().toISOString().slice(0, 10);
    const active = new Set(['OPEN', 'PARTIAL_KOT', 'KOT_SENT', 'PREPARING', 'READY', 'DRAFT', 'SENT_TO_BAR']);
    const cutoff = new Date(Date.now() - 30 * 60 * 1000);

    const sessionByTid = {};
    for (const s of (allTableSessions || [])) {
      const tid = String(s.table_id || s.kot_table_id || '');
      if (tid) sessionByTid[tid] = s;
    }

    const ordersByTid = {};
    for (const o of (recentOrders || [])) {
      const tid = String(o.table_id || '');
      if (!tid) continue;
      if ((o.business_date || o.opened_at || '').toString().slice(0, 10) !== today) continue;
      if (!ordersByTid[tid]) ordersByTid[tid] = [];
      ordersByTid[tid].push(o);
    }

    const allTids = new Set([...Object.keys(sessionByTid), ...Object.keys(ordersByTid)]);

    for (const tid of allTids) {
      if (clearedTids.has(tid)) { map[tid] = { status: 'vacant' }; continue; }

      const session = sessionByTid[tid];
      const orders = ordersByTid[tid] || [];

      if (session) {
        const activeOrders = orders.filter(o => active.has(String(o.status || '').toUpperCase()));
        const pendingKotCount = activeOrders.filter(o =>
          ['KOT_SENT', 'PREPARING'].includes(String(o.status || '').toUpperCase())
        ).length;
        const totalAmount = activeOrders.reduce((s, o) => s + Number(o.order_total || 0), 0);
        const sorted = [...activeOrders].sort((a, b) => new Date(b.opened_at) - new Date(a.opened_at));
        const latest = sorted[0];
        const linkRole = String(session.link_role || 'STANDALONE').toUpperCase();
        map[tid] = {
          status: 'occupied',
          openedAt: session.opened_at,
          linkRole,
          isLinked: linkRole !== 'STANDALONE' || !!session.linked_to_session_id,
          waiter: latest?.created_by_username || '',
          pax: latest?.guest_count || null,
          totalAmount,
          pendingKotCount,
          lastActivityMins: latest
            ? Math.max(0, Math.floor((Date.now() - new Date(latest.opened_at)) / 60000))
            : null,
        };
      } else {
        const settled = orders.filter(o => {
          const st = String(o.status || '').toUpperCase();
          if (!['BILLED', 'CLOSED'].includes(st)) return false;
          const ca = o.closed_at ? new Date(o.closed_at) : null;
          return ca && ca > cutoff;
        });
        if (settled.length > 0) {
          const last = settled.sort((a, b) => new Date(b.closed_at) - new Date(a.closed_at))[0];
          map[tid] = {
            status: 'needs_cleared',
            settledMinsAgo: Math.max(0, Math.floor((Date.now() - new Date(last.closed_at)) / 60000)),
          };
        }
      }
    }
    return map;
  }, [allTableSessions, recentOrders, clearedTids]);

  // Status counts across ALL tables (unfiltered by search/status), matching
  // the same statusMap[tid] || vacant fallback the cards below render with.
  const statusCounts = useMemo(() => {
    const counts = { ALL: tables.length, occupied: 0, vacant: 0, needs_cleared: 0 };
    for (const t of tables) {
      const st = (statusMap[String(t.id)] || { status: 'vacant' }).status;
      counts[st] = (counts[st] || 0) + 1;
    }
    return counts;
  }, [tables, statusMap]);

  const grouped = useMemo(() => {
    const q = search.toLowerCase();
    const filtered = tables.filter(t => {
      if (statusFilter !== 'ALL') {
        const st = (statusMap[String(t.id)] || { status: 'vacant' }).status;
        if (st !== statusFilter) return false;
      }
      if (!q) return true;
      return String(t.table_no || '').toLowerCase().includes(q) ||
             String(t.zone || '').toLowerCase().includes(q);
    });
    const groups = {};
    for (const t of filtered) {
      const zone = (String(t.zone || '').trim() || 'General');
      if (!groups[zone]) groups[zone] = [];
      groups[zone].push(t);
    }
    return groups;
  }, [tables, search, statusFilter, statusMap]);

  const zones = Object.keys(grouped).sort();
  const flatFiltered = useMemo(() => zones.flatMap(z => grouped[z]), [zones, grouped]);
  const occupiedPct = statusCounts.ALL ? Math.round((statusCounts.occupied / statusCounts.ALL) * 100) : 0;
  const vacantPct = statusCounts.ALL ? Math.round((statusCounts.vacant / statusCounts.ALL) * 100) : 0;
  const needsClearedPct = statusCounts.ALL ? Math.round((statusCounts.needs_cleared / statusCounts.ALL) * 100) : 0;

  const btn = { borderRadius: 7, border: 'none', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer', padding: '7px 10px', lineHeight: 1.3 };

  return (
    <div style={{ padding: isMobile ? '1rem' : '1.5rem 2rem' }}>

      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div onClick={onBack} style={{ cursor: 'pointer', color: '#555', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
          &#8592; Back
        </div>
        <input
          type="text"
          placeholder="Search tables…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 130, maxWidth: 280, padding: '0.42rem 0.7rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.87rem', outline: 'none' }}
        />
        {[['ALL', 'All'], ['vacant', 'Vacant'], ['occupied', 'Occupied'], ['needs_cleared', 'Needs Clearing']].map(([key, label]) => {
          const active = statusFilter === key;
          const color = key === 'occupied' ? '#dc2626' : key === 'vacant' ? '#16a34a' : key === 'needs_cleared' ? '#d97706' : '#334155';
          return (
            <button key={key} type="button" onClick={() => setStatusFilter(key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5, padding: '0.4rem 0.7rem', borderRadius: 8,
                border: active ? `1.5px solid ${color}` : '1px solid #e5e7eb',
                background: active ? `${color}14` : '#fff', color: active ? color : '#6b7280',
                fontWeight: 700, fontSize: '0.76rem', cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
              {label} ({statusCounts[key] || 0})
            </button>
          );
        })}
        <div style={{ display: 'flex', gap: 0, marginLeft: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          {[['FLOOR', '⊞ Floor View'], ['LIST', '☰ List View']].map(([key, label]) => (
            <button key={key} type="button" onClick={() => setViewMode(key)}
              style={{ padding: '0.4rem 0.75rem', border: 'none', background: viewMode === key ? '#0d9488' : '#fff', color: viewMode === key ? '#fff' : '#6b7280', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}
            >{label}</button>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: '0.75rem', marginBottom: '1.25rem' }}>
        {[
          { label: 'Total Tables', value: statusCounts.ALL, bg: '#eff6ff', color: '#1d4ed8' },
          { label: 'Vacant', value: statusCounts.vacant || 0, pct: `${vacantPct}%`, bg: '#f0fdf4', color: '#16a34a' },
          { label: 'Occupied', value: statusCounts.occupied || 0, pct: `${occupiedPct}%`, bg: '#fef2f2', color: '#dc2626' },
          { label: 'Needs Clearing', value: statusCounts.needs_cleared || 0, pct: `${needsClearedPct}%`, bg: '#fffbeb', color: '#d97706' },
        ].map((s) => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: '0.7rem 0.9rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '1.3rem', fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{s.label}</div>
            </div>
            {s.pct && <div style={{ fontSize: '0.8rem', fontWeight: 700, color: s.color }}>{s.pct}</div>}
          </div>
        ))}
      </div>

      {viewMode === 'FLOOR' ? zones.map(zone => (
        <div key={zone} style={{ marginBottom: '2rem' }}>
          <div style={{ fontSize: '0.74rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', marginBottom: '0.7rem' }}>
            {zone}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(auto-fill,minmax(220px,1fr))', gap: '0.9rem' }}>
            {grouped[zone].map(table => {
              const tid = String(table.id);
              const info = statusMap[tid] || { status: 'vacant' };
              const { status } = info;
              const cfg = STATUS_CFG[status];
              const name = table.table_no || `Table ${tid.slice(-4)}`;
              const capacity = Number(table.capacity || 4);
              const readyOrderIds = tableReadyToBill[tid];
              const isReady = !!readyOrderIds;
              const dur = humanDuration(info.openedAt);

              return (
                <div key={tid} style={{ borderRadius: 12, border: `1px solid ${cfg.border}`, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 6px rgba(0,0,0,0.07)', background: '#fff' }}>

                  {/* Coloured header band */}
                  <div style={{ background: cfg.headerBg, padding: '10px 12px 9px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
                    <div>
                      <div style={{ color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>{name}</div>
                      <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.73rem', marginTop: 3 }}>
                        {status === 'occupied'      ? `Occupied (${info.pax || 0}/${capacity} Pax)` :
                         status === 'needs_cleared' ? 'Needs Cleared' : 'Vacant'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}>
                      <div style={{ background: 'rgba(255,255,255,0.22)', borderRadius: 5, padding: '2px 6px', display: 'flex', alignItems: 'center', gap: 3, fontSize: '0.74rem', color: '#fff', fontWeight: 600 }}>
                        &#128100; {capacity}
                      </div>
                      {status === 'occupied' && <PaxIndicator pax={info.pax} capacity={capacity} />}
                    </div>
                  </div>

                  {/* Card body */}
                  <div style={{ padding: '10px 12px 6px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>

                    {status === 'occupied' && (
                      <>
                        {info.isLinked && (
                          <div style={{ alignSelf: 'flex-start', background: '#ede9fe', border: '1px solid #c4b5fd', color: '#6d28d9', borderRadius: 6, padding: '2px 8px', fontSize: '0.71rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                            &#128279; Linked{info.linkRole === 'PRIMARY' ? ' (Primary)' : info.linkRole === 'SECONDARY' ? ' (Combined bill)' : ''}
                          </div>
                        )}
                        <div style={{ fontSize: '0.8rem', color: '#4b5563', display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ color: '#9ca3af' }}>&#128100;</span>
                          {info.waiter
                            ? <span>Assigned: <strong>{info.waiter}</strong></span>
                            : <span style={{ color: '#9ca3af' }}>No server assigned</span>}
                        </div>

                        {dur && (
                          <div style={{ background: '#f9fafb', borderRadius: 8, padding: '7px 10px', border: '1px solid #f3f4f6' }}>
                            <div style={{ fontSize: '0.81rem', fontWeight: 600, color: '#111827' }}>
                              &#9203;&nbsp;Seated for {dur}
                            </div>
                            {info.lastActivityMins != null && (
                              <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 2 }}>
                                Last activity:&nbsp;
                                {info.lastActivityMins < 1 ? 'just now' : `${info.lastActivityMins} min${info.lastActivityMins !== 1 ? 's' : ''} ago`}
                              </div>
                            )}
                          </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 2 }}>
                          <div>
                            <div style={{ fontSize: '0.69rem', color: '#9ca3af', marginBottom: 1 }}>Current Total</div>
                            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
                              &#8377;{Number(info.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </div>
                          </div>
                          {info.pendingKotCount > 0 && (
                            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 5, padding: '2px 7px', fontSize: '0.71rem', fontWeight: 700, color: '#92400e' }}>
                              {info.pendingKotCount} KOT{info.pendingKotCount !== 1 ? 's' : ''} pending
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {status === 'vacant' && (
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '10px 0', gap: 4 }}>
                        <div style={{ fontSize: '1.5rem' }}>&#127869;</div>
                        <div style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Available for seating</div>
                        <div style={{ fontSize: '0.71rem', color: '#d1d5db' }}>Capacity: {capacity} guests</div>
                      </div>
                    )}

                    {status === 'needs_cleared' && (
                      <div style={{ flex: 1, padding: '6px 0', display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#b45309' }}>
                          &#9888;&#65039;&nbsp;Recently vacated
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#78716c' }}>
                          {info.settledMinsAgo != null
                            ? (info.settledMinsAgo < 1 ? 'Settled just now' : `Settled ${info.settledMinsAgo}m ago`)
                            : 'Please clear for next guest'}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div style={{ padding: '6px 10px 10px', borderTop: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: 5 }}>

                    {status === 'occupied' && (
                      <>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button onClick={() => onSelect(table)} style={{ ...btn, flex: 1, background: '#0d9488', color: '#fff' }}>
                            View &amp; Add Items
                          </button>
                          {onPrintKot && (
                            <button onClick={() => onPrintKot(table)} style={{ ...btn, background: '#fff', border: '1px solid #d1d5db', color: '#374151' }}>
                              Print KOT
                            </button>
                          )}
                        </div>
                        {isReady && (
                          <button
                            onClick={e => { e.stopPropagation(); onGenerateTableBill?.(table, readyOrderIds); }}
                            style={{ ...btn, background: '#16a34a', color: '#fff', width: '100%', fontWeight: 700 }}
                          >
                            &#10003;&nbsp;Settle Bill
                          </button>
                        )}
                      </>
                    )}

                    {status === 'vacant' && (
                      <div style={{ display: 'flex', gap: 5 }}>
                        <button onClick={() => onSelect(table)} style={{ ...btn, flex: 1, background: '#16a34a', color: '#fff' }}>
                          Occupy &amp; Order
                        </button>
                        <button disabled title="Reservations coming soon" style={{ ...btn, background: '#f9fafb', border: '1px solid #e5e7eb', color: '#d1d5db', cursor: 'not-allowed' }}>
                          Reserve
                        </button>
                      </div>
                    )}

                    {status === 'needs_cleared' && (
                      <button
                        onClick={() => setClearedTids(prev => new Set([...prev, tid]))}
                        style={{ ...btn, background: '#d97706', color: '#fff', width: '100%', fontWeight: 700 }}
                      >
                        &#10003;&nbsp;Clear Table Now
                      </button>
                    )}

                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )) : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 110px 130px 140px', gap: 8, padding: '0.6rem 0.9rem', background: '#f8fafc', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
            <span>Table</span><span>Zone</span><span>Pax</span><span>Status</span><span>Action</span>
          </div>
          {flatFiltered.map(table => {
            const tid = String(table.id);
            const info = statusMap[tid] || { status: 'vacant' };
            const { status } = info;
            const cfg = STATUS_CFG[status];
            const name = table.table_no || `Table ${tid.slice(-4)}`;
            const capacity = Number(table.capacity || 4);
            const zone = String(table.zone || '').trim() || 'General';
            return (
              <div key={tid} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 110px 130px 140px', gap: 8, padding: '0.6rem 0.9rem', borderTop: '1px solid #f1f5f9', alignItems: 'center', fontSize: '0.83rem' }}>
                <span style={{ fontWeight: 700 }}>{name}</span>
                <span style={{ color: '#64748b' }}>{zone}</span>
                <span>{status === 'occupied' ? `${info.pax || 0}/${capacity}` : `${capacity} cap`}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: cfg.headerBg, fontWeight: 600 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.headerBg }} />
                  {cfg.label}
                </span>
                <button onClick={() => onSelect(table)} style={{ ...btn, background: status === 'occupied' ? '#0d9488' : '#16a34a', color: '#fff' }}>
                  {status === 'occupied' ? 'View & Add' : 'Occupy & Order'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {flatFiltered.length === 0 && (
        <div style={{ textAlign: 'center', color: '#9ca3af', padding: '3rem 0', fontSize: '0.9rem' }}>No tables found</div>
      )}
    </div>
  );
}
